import React from 'react';
import {getWholeDivWrapperClass,} from "./sharedStyles.tsx";
import {
    CompSelectType,
    CompSummaryComponent,
    FormComponentProps, ImageUploadComponent,
} from "../DynamicFormTypes.ts";
import {formatCurrency, formatDateNumeric} from "../../../utils/dateUtils.ts";
import {isNumber} from "lodash";
import {useWatch} from "react-hook-form";
import {useFormMethods} from "../DyanmicFormContext.tsx";
import {useDeepStableMemo} from "../../../utils/useMemoNoRefrence.ts";
import {emitEvent} from "../../../context/EventBus.ts";
import {ScanSearch} from "lucide-react";
import {ImageUpload} from "./image/ImageUpload.tsx";
import {FormImageType} from "../../../api/ApiTypes.ts";

const COMP_TYPES = {
    LISTING: 'listingComp',
    SALE: 'saleComp'
} as const;

const SUBJECT_FIELDS = {
    yearBuilt: 'subjectPropertyYearBuilt',
    land: 'subjectPropertyLand',
    gla: 'subjectPropertyGla',
    rooms: 'subjectPropertyRooms',
    beds: 'subjectPropertyBeds',
    baths: 'subjectPropertyBaths',
    halfBath: 'subjectPropertyHalfBath'
} as const;

interface ComparableData {
    saleStatus?: string;
    compType?: CompSelectType;
    price?: string;
    date?: string;
    addressLine1?: string;
    addressLine2?: string;
    beds?: string | number;
    baths?: string | number;
    halfBaths?: string | number;
    sqft?: string | number;
    lotSize?: string | number;
    yearBuilt?: string | number;
    bedsDiff?: { isPositive: boolean; absoluteDiff: number } | null;
    bathsDiff?: { isPositive: boolean; absoluteDiff: number } | null;
    halfBathsDiff?: { isPositive: boolean; absoluteDiff: number } | null;
    sqftDiff?: { isPositive: boolean; absoluteDiff: number } | null;
    lotSizeDiff?: { isPositive: boolean; absoluteDiff: number } | null;
    yearBuiltDiff?: { isPositive: boolean; absoluteDiff: number } | null;
    imageUrl?: string;
}

const calculateDifference = (subject: number, comparable: number) => {
    if (!isNumber(subject) || !isNumber(comparable)) return null;
    const diff = comparable - subject;
    if (diff === 0) return null;

    return {
        isPositive: diff > 0,
        absoluteDiff: Math.abs(diff),
    };
};

const transformComparableData = (comparable: any, compType: string) => {
    const getField = (key: string) => comparable[`${compType}${key}`];

    const saleStatus = getField("SaleType") == null ? "-" : compType == COMP_TYPES.SALE ? "Sold" : "Active";

    const city = getField("City");
    const state = getField("State");
    const zip = getField("Zip");

    let addressLine2 = "-";
    if (city || state || zip) {
        const parts = [];
        if (city) parts.push(city);

        // Combine state and zip with a space (no comma)
        if (state && zip) {
            parts.push(`${state} ${zip}`);
        } else if (state) {
            parts.push(state);
        } else if (zip) {
            parts.push(zip);
        }

        addressLine2 = parts.join(", ");
    }

    return {
        saleStatus,
        price: formatCurrency(
            compType === COMP_TYPES.SALE ? getField("LastSoldPrice") : getField("ListPrice"),
            "USD",
            {style: "currency", currency: "USD", maximumFractionDigits: 0}
        ),
        date: formatDateNumeric(
            compType === COMP_TYPES.SALE ? getField("LastSoldDate") : getField("ListDate")
        ),
        addressLine1: getField("Address") ?? "-",
        addressLine2: addressLine2,
        beds: getField("BedroomCount") ?? "-",
        baths: getField("FullBathroomCount") ?? "-",
        halfBaths: getField("PartialBathroomCount") ?? "-",
        sqft: getField("GrossLivingArea") ?? "-",
        lotSize: getField("LotSize") ?? "-",
        yearBuilt: getField("YearBuilt") ?? "-",
    };
};

const CompSummaryRenderer: React.FC<FormComponentProps<CompSummaryComponent>> = ({component}) => {

    const {formMethods} = useFormMethods();
    const {control} = formMethods;

    const [grid, index, type] = component.fieldKey.split(".");

    let compType: string;
    if (type?.startsWith("list")) {
        compType = COMP_TYPES.LISTING;
    } else if (type?.startsWith("sale")) {
        compType = COMP_TYPES.SALE;
    }
    ;

    const watchedValues = useWatch({
        control: control,
        name: Object.values(SUBJECT_FIELDS),
        disabled: Object.values(SUBJECT_FIELDS).length === 0
    });
    const watchedGrids = useWatch({
        control: control,
        name: grid,
        defaultValue: []
    });

    const getSubjectValue = (fieldName: keyof typeof SUBJECT_FIELDS): number => {
        const fieldKey = SUBJECT_FIELDS[fieldName];
        const index = Object.values(SUBJECT_FIELDS).indexOf(fieldKey);
        return parseInt(watchedValues[index]?.valueOf());
    };

    const calculateValue = (): ComparableData => {
        const currentComparable = watchedGrids?.[index];
        if (!currentComparable) return {};

        const data = transformComparableData(currentComparable, compType);

        return {
            ...data,
            bedsDiff: calculateDifference(getSubjectValue('beds'), parseInt(data.beds)),
            bathsDiff: calculateDifference(getSubjectValue('baths'), parseInt(data.baths)),
            halfBathsDiff: calculateDifference(getSubjectValue('halfBath'), parseInt(data.halfBaths)),
            sqftDiff: calculateDifference(getSubjectValue('gla'), parseInt(data.sqft)),
            lotSizeDiff: calculateDifference(getSubjectValue('land'), parseInt(data.lotSize)),
            yearBuiltDiff: calculateDifference(getSubjectValue('yearBuilt'), parseInt(data.yearBuilt)),
        };
    };

    const derivedValue = useDeepStableMemo<ComparableData>(() => {
        return calculateValue();
    }, [watchedValues, watchedGrids]);

    const handleClickComp = (): void => {
        emitEvent("COMPARABLE_CLICKED", {
            fieldKey: grid,
            index: parseInt(index)
        });
    };

    const handleClickCompImage = (): void => {
        emitEvent("COMPARABLE_IMAGE_CLICKED", {
            fieldKey: grid,
            index: parseInt(index)
        });
    };

    const getStatusBgColor = () => {
        if (!derivedValue.saleStatus || derivedValue.saleStatus === "-") return "bg-gray-300";
        return compType === COMP_TYPES.SALE ? "bg-blue-500" : "bg-green-500";
    };

    const renderDiff = (diff: {
        absoluteDiff: number,
        isPositive: boolean
    } | null | undefined, label: string, unit: string) => {
        if (!diff || !diff.absoluteDiff) return null;
        return (
            <div className="ml-2 text-gray-700 text-xs">
                <span style={{color: diff.isPositive ? "green" : "red"}}>
                    {diff.isPositive ? "▲" : "▼"}{" "}
                </span>
                <span>
                    {diff.absoluteDiff} {unit} {diff.isPositive ? `more ${label}` : `less ${label}`}
                </span>
            </div>
        );
    };

    return (
        <div className="w-full max-h-111 min-h-111 justify-start flex flex-col mb-0.5 cursor-pointer">
            <div
                className={getWholeDivWrapperClass(component) + " overflow-visible mt-0 justify-center"}
                role="button"
                onClick={handleClickComp}
                aria-label="Open Flyout"
            >
                <div className="relative shrink-0">
                    <ImageUpload component={{
                        requiredType: FormImageType.COMPARABLE,
                        fieldKey: component.fieldKey,
                        label: component.label,
                        compactUI: true,
                        validationRules: component.validationRules
                    } as ImageUploadComponent}/>

                    {/* Open Comp CA Image Modal Button */}
                    {derivedValue.saleStatus != "-" &&
                        <button
                            className="absolute bottom-1 right-1 bg-black/20 hover:bg-black/50 p-1 rounded-full transition-colors z-10"
                            onClick={(e) => {
                                handleClickCompImage();
                                e.preventDefault();
                            }}
                        >
                            <ScanSearch className="h-4 w-4 text-white"/>
                        </button>
                    }
                </div>
                <div

                >
                    <div
                        className="bg-gray-200 rounded-t-sm flex flex-col items-center justify-center h-12 overflow-auto">
                        <div className="text-sm font-medium text-gray-700">{derivedValue.addressLine1 ?? ""}</div>
                        <div className="text-sm font-medium text-gray-700">{derivedValue.addressLine2 ?? ""}</div>
                    </div>
                    <div
                        className={`rounded-b-sm flex flex-col items-center justify-center h-6 ${getStatusBgColor()}`}>
                        <div className="text-sm font-medium text-gray-700">{derivedValue.saleStatus}</div>
                    </div>
                    <div className="ml-2 mt-2 text-gray-700 text-sm font-medium">{derivedValue.price}</div>
                    <div className="ml-2 mb-2 text-gray-700 text-sm">{derivedValue.saleStatus} {derivedValue.date}</div>
                    <div
                        className="ml-2 text-gray-700 text-sm">{derivedValue.beds} br, {derivedValue.baths} ba, {derivedValue.sqft} sq
                        ft
                    </div>
                    {renderDiff(derivedValue.bedsDiff, "bedroom(s)", "")}
                    {renderDiff(derivedValue.bathsDiff, "bathroom(s)", "")}
                    {renderDiff(derivedValue.halfBathsDiff, "half bathroom(s)", "")}
                    {renderDiff(derivedValue.sqftDiff, "sq ft", "sq ft")}
                    {renderDiff(derivedValue.lotSizeDiff, "lot", "sq ft")}
                    {renderDiff(derivedValue.yearBuiltDiff, "year(s)", "")}
                </div>

            </div>

        </div>
    );
}

export const CompSummary = React.memo(CompSummaryRenderer);

